import React, { useState, useEffect } from 'react';
import { Music2 } from 'lucide-react';

/**
 * BoothAlbumArt — static cover art for the Control Booth's Master Player card.
 *
 * Replaces the old BoothVideoTile, which was an INDEPENDENT muted YouTube
 * player that autoplayed + looped on its own, completely disconnected from the
 * audio dual-driver's play/pause state. That caused the reported bug: pausing
 * the music left the video looping (and vice-versa), and the two never matched.
 * Since the on-screen video was never linked to the audio, we show album art
 * instead — the hidden dual-driver in utils/youtubePlayer.js still owns audio.
 *
 * Art source priority:
 *   1. track album art (album_image / artwork_url)
 *   2. YouTube hqdefault thumbnail for the current videoId (always exists)
 *   3. music-note glyph
 */
export default function BoothAlbumArt({ videoId, albumImage, alt = '' }) {
  const ytThumb = typeof videoId === 'string' && /^[A-Za-z0-9_-]{11}$/.test(videoId)
    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
    : '';
  const [src, setSrc] = useState(albumImage || ytThumb);

  useEffect(() => { setSrc(albumImage || ytThumb); }, [albumImage, ytThumb]);

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        data-testid="master-album-art"
        onError={() => {
          // Album art URL failed → try the YouTube thumb → then the glyph.
          if (src !== ytThumb && ytThumb) setSrc(ytThumb);
          else setSrc('');
        }}
      />
    );
  }

  return (
    <div
      className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-black"
      data-testid="master-album-art-fallback"
    >
      <Music2 className="w-14 h-14 text-zinc-700" />
    </div>
  );
}
